import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeNGConfig } from 'primeng/api';
import { Subscription } from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatOption,
  MatSelect,
  MatSelectModule,
} from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CalendarModule } from 'primeng/calendar';
import { SidebarModule } from 'primeng/sidebar';
import { EventFilterConfig } from '../../../models/event/filter';
import { CategoryDisplayLabelPipe } from '../../../shared/category-ordering/category-display-label.pipe';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';

@Component({
  selector: 'stupa-event-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatSelect,
    MatOption,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    TranslateModule,
    SidebarModule,
    CalendarModule,
    CategoryDisplayLabelPipe,
    EmptyStateComponent,
    SkeletonLoaderComponent,
  ],
  templateUrl: './event-filter.component.html',
  styleUrl: './event-filter.component.scss',
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventFilterComponent implements OnInit, OnChanges, OnDestroy {
  private langChangeSub?: Subscription;
  @Input() addedCategories: any[] = [];
  @Input() addedStages: any[] = [];
  @Input() addedTime: any[] = [];
  @Input() venue_added_list: any[] = [];
  @Input() addedTable: any[] = [];
  @Input() addedStatus: any[] = [];
  @Input() start_time: any = '';
  @Input() end_time: any = '';
  @Input() selectedItemsMap: { [key: string]: any[] } = {};
  @Input() category_list: any = [];
  @Input() stages_list: any[] = [];
  @Input() venueDetails: any[] = [];
  @Input() total_tables: any[] = [];
  @Input() match_status_list: any[] = [];
  /** When true, Match Status allows only one selected value at a time. */
  @Input() singleStatusSelection = false;
  @Input() parent_role_list: any[] = [];
  @Input() roleCheckboxValues: { [key: string]: boolean } = {
    Member: false,
    Club: false,
  };
  @Input() filterConfig: EventFilterConfig = {
    category: false,
    stage: false,
    venue: false,
    table: false,
    status: false,
    time: false,
    roles: true,
  };
  @Input() is_video_filter: boolean = false;

  @Output() addCategories = new EventEmitter<any>();
  @Output() removeCategory = new EventEmitter<any>();
  @Output() addStages = new EventEmitter<any>();
  @Output() removeStage = new EventEmitter<any>();
  @Output() addVenues = new EventEmitter<any>();
  @Output() remove_venues = new EventEmitter<any>();
  @Output() addTable = new EventEmitter<any>();
  @Output() removeTable = new EventEmitter<any>();
  @Output() timeSelected = new EventEmitter<any>();
  @Output() addStatus = new EventEmitter<any>();
  @Output() removeStatus = new EventEmitter<any>();
  @Output() removeTime = new EventEmitter<any>();
  @Output() selectionChange = new EventEmitter<any>();
  @Output() removeItem = new EventEmitter<any>();
  @Output() clear_filters = new EventEmitter<any>();
  @Output() apply_changes = new EventEmitter<any>();
  @Output() closeFilters = new EventEmitter<any>();
  @Input() parentRoleLoading: { [roleName: string]: boolean } = {};
  @Input() parentRoleHasMore: { [roleName: string]: boolean } = {};
  /** When set (and available), select this left-nav tab on open / change. */
  @Input() preferredTab: string | null = null;
  @Output() roleClicked = new EventEmitter<string>();
  @Output() loadMoreRoles = new EventEmitter<string>();
  @Output() roleSearch = new EventEmitter<{ roleName: string; search: string }>();

  @Input() events_list: any[] = [];
  @Input() stream_status: any[] = [];
  @Input() disabledSports: any[] = [];

  @Input() selectedStatuses: any[] = [];

  @Output() eventSelection = new EventEmitter<{ event: any; index: number }>();
  @Output() categorySelection = new EventEmitter<any>();
  @Output() statusSelectionChange = new EventEmitter<any>();
  @Output() removeStatusItem = new EventEmitter<any>();
  @Output() timeFilterStateChange = new EventEmitter<boolean>();

  filterApplied: {
    category: boolean;
    stage: boolean;
    venue: boolean;
    table: boolean;
    status: boolean;
    time: boolean;
    roles: { [key: string]: boolean };
  } = {
    category: false,
    stage: false,
    venue: false,
    table: false,
    status: false,
    time: false,
    roles: {},
  };

  visible = true;
  innerWidth: any;
  event_type: any;
  activeTab: string = '';
  roleSearchValues: { [roleName: string]: string } = {};

  /**
   * Role tab currently active in the left nav, when that tab supports
   * header search (excludes YOB year-picker). Used by the header search bar.
   */
  get activeSearchableRole(): any | null {
    if (!this.activeTab?.startsWith('role_')) {
      return null;
    }
    const roleName = this.activeTab.slice('role_'.length);
    if (!roleName || roleName === 'YOB') {
      return null;
    }
    return (
      (this.parent_role_list || []).find(
        (role: any) => role?.parent_role_name === roleName,
      ) ?? null
    );
  }

  @HostListener('window:resize')
  onResize() {
    this.innerWidth = window.innerWidth;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translateService: TranslateService,
    private primengConfig: PrimeNGConfig
  ) {
    this.innerWidth = window.innerWidth;
    this.event_type = localStorage.getItem('event_type') || '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parent_role_list'] || changes['parentRoleLoading'] || changes['parentRoleHasMore']) {
      this.cdr.markForCheck();
    }
    if ((changes['filterConfig'] || changes['parent_role_list'] || changes['is_video_filter']) && !this.isTabAvailable(this.activeTab)) {
      this.activeTab = this.getFirstAvailableTab();
    }
    if (changes['preferredTab'] && this.preferredTab) {
      this.applyPreferredTab(this.preferredTab);
    }
  }

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
    if (this.preferredTab && this.isTabAvailable(this.preferredTab)) {
      this.applyPreferredTab(this.preferredTab);
    } else {
      this.activeTab = this.getFirstAvailableTab();
    }
    this.handleSelection('category', this.addedCategories);
    this.handleSelection('stage', this.addedStages);
    this.handleSelection('venue', this.venue_added_list);
    this.handleSelection('table', this.addedTable);
    this.handleSelection('status', this.addedStatus);
    this.handleSelection('start', this.start_time);
    this.handleSelection('end', this.end_time);
    this.handleSelection('roles', this.selectedItemsMap);

    // Initialize time filter state
    this.filterApplied.time =
      (this.start_time != null && this.start_time !== '') ||
      (this.end_time != null && this.end_time !== '');
    this.timeFilterStateChange.emit(this.filterApplied.time);
    this.event_type = localStorage.getItem('event_type') || '';

    this.updateCalendarLocale();
    this.langChangeSub = this.translateService.onLangChange.subscribe(() => {
      this.updateCalendarLocale();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  private updateCalendarLocale(): void {
    const lang = this.translateService.currentLang || this.translateService.defaultLang || 'en';
    const locale = this.getLocaleForLanguage(lang);
    const monthNames: string[] = [];
    const monthNamesShort: string[] = [];
    const dayNames: string[] = [];
    const dayNamesShort: string[] = [];
    const dayNamesMin: string[] = [];
    for (let m = 0; m < 12; m++) {
      const d = new Date(2024, m, 1);
      monthNames.push(new Intl.DateTimeFormat(locale, { month: 'long' }).format(d));
      monthNamesShort.push(new Intl.DateTimeFormat(locale, { month: 'short' }).format(d));
    }
    for (let d = 0; d < 7; d++) {
      const date = new Date(2024, 0, 7 + d);
      dayNames.push(new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date));
      dayNamesShort.push(new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date));
      dayNamesMin.push(new Intl.DateTimeFormat(locale, { weekday: 'narrow' }).format(date));
    }
    this.primengConfig.setTranslation({
      monthNames,
      monthNamesShort,
      dayNames,
      dayNamesShort,
      dayNamesMin,
      today: new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).formatToParts(new Date()).find(p => p.type === 'literal')?.value ? 'Today' : 'Today',
      clear: 'Clear',
    });
  }

  private getLocaleForLanguage(lang: string): string {
    const langMap: Record<string, string> = {
      en: 'en-US',
      de: 'de-DE',
      fr: 'fr-FR',
      it: 'it-IT',
      spain: 'es-ES',
      norway: 'nb-NO',
      swedan: 'sv-SE',
      sv: 'sl-SI',
      cs: 'cs-CZ',
      da: 'da-DK',
    };
    return langMap[lang] || 'en-US';
  }

  handleSelection = (key: string, value: any) => {
    if (key === 'roles') {
      const roleMap: { [key: string]: boolean } = {};
      Object.keys(value).forEach((key: string) => {
        roleMap[key] = value[key].length > 0;
      });
      this.filterApplied = {
        ...this.filterApplied,
        roles: roleMap,
      };
    } else if (key === 'start') {
      const timeSelected =
        (value != null && value !== '') ||
        (this.end_time != null && this.end_time !== '');
      this.filterApplied = {
        ...this.filterApplied,
        time: timeSelected,
      };
      this.timeFilterStateChange.emit(timeSelected);
    } else if (key === 'end') {
      const timeSelected =
        (value != null && value !== '') ||
        (this.start_time != null && this.start_time !== '');
      this.filterApplied = {
        ...this.filterApplied,
        time: timeSelected,
      };
      this.timeFilterStateChange.emit(timeSelected);
    } else {
      this.filterApplied = {
        ...this.filterApplied,
        [key]: value.length > 0,
      };
    }
  };

  filterSelected = (key: string, event: any) => {
    if (key === 'category') {
      this.addCategories.emit(event);
      this.handleSelection(key, event.value);
    }
    if (key === 'stage') {
      this.addStages.emit(event);
      this.handleSelection(key, event.value);
    }
    if (key === 'venue') {
      this.addVenues.emit(event);
      this.handleSelection(key, event.value);
    }
    if (key === 'table') {
      this.addTable.emit(event);
      this.handleSelection(key, event.value);
    }
    if (key === 'status') {
      this.addStatus.emit(event);
      this.handleSelection(key, event.value);
    }
    if (key === 'start') {
      this.timeSelected.emit({ event, key });
      this.handleSelection(key, event);
    }
    if (key === 'end') {
      this.timeSelected.emit({ event, key });
      this.handleSelection(key, event);
    }
  };

  // Generic list toggle used by every checkbox-list filter panel (category,
  // stage, venue, table, status, roles) — mirrors what PrimeNG's p-multiSelect
  // onChange used to hand back (`value` = new full array, `itemValue` = the
  // toggled row), so downstream parent handlers keep working unmodified.
  private toggleArrayItem<T>(list: T[], item: T, matches: (a: T, b: T) => boolean): T[] {
    return list.some((existing) => matches(existing, item))
      ? list.filter((existing) => !matches(existing, item))
      : [...list, item];
  }

  isCategorySelected(item: any): boolean {
    return this.addedCategories.some((c) => c.category_id === item.category_id);
  }

  toggleCategory(item: any): void {
    const value = this.toggleArrayItem(this.addedCategories, item, (a, b) => a.category_id === b.category_id);
    this.filterSelected('category', { value, itemValue: item });
  }

  isStageSelected(item: any): boolean {
    return this.addedStages.some((s) => s.id === item.id);
  }

  toggleStage(item: any): void {
    const value = this.toggleArrayItem(this.addedStages, item, (a, b) => a.id === b.id);
    this.filterSelected('stage', { value, itemValue: item });
  }

  isVenueSelected(item: any): boolean {
    return this.venue_added_list.some((v) => v.id === item.id);
  }

  toggleVenue(item: any): void {
    const value = this.toggleArrayItem(this.venue_added_list, item, (a, b) => a.id === b.id);
    this.filterSelected('venue', { value, itemValue: item });
  }

  isTableSelected(item: any): boolean {
    return this.addedTable.some((t) => t.table_number === item.table_number);
  }

  toggleTable(item: any): void {
    const value = this.toggleArrayItem(this.addedTable, item, (a, b) => a.table_number === b.table_number);
    this.filterSelected('table', { value, itemValue: item });
  }

  /** Clears table selections only; other filter types are untouched. */
  clearTableSelections(): void {
    this.filterSelected('table', { value: [] });
    this.cdr.markForCheck();
  }

  isStatusSelected(item: any): boolean {
    return this.addedStatus.some((s) => s.status_id === item.status_id);
  }

  toggleStatus(item: any): void {
    let value: any[];
    if (this.singleStatusSelection) {
      value = this.isStatusSelected(item) ? [] : [item];
    } else {
      value = this.toggleArrayItem(this.addedStatus, item, (a, b) => a.status_id === b.status_id);
    }
    this.filterSelected('status', { value, itemValue: item });
  }

  isVideoStatusSelected(item: any): boolean {
    return this.selectedStatuses.includes(item.value);
  }

  toggleVideoStatus(item: any): void {
    const value = this.selectedStatuses.includes(item.value)
      ? this.selectedStatuses.filter((v: any) => v !== item.value)
      : [...this.selectedStatuses, item.value];
    this.onStatusChange({ value });
  }

  roleSelected(event: any, item: any) {
    // Mirrors what the old p-multiSelect [(ngModel)] used to do: mutate the
    // shared selectedItemsMap object in place so parents that read it back
    // off the same @Input reference (rather than re-assigning it from
    // event.value in their own selectionChange) stay in sync.
    this.selectedItemsMap[item.parent_role_name] = event.value;
    this.selectionChange.emit({ event, item });
    this.filterApplied = {
      ...this.filterApplied,
      roles: {
        ...this.filterApplied.roles,
        [item.parent_role_name]: event.value.length > 0,
      },
    };
  }

  roleRemoved(roleName: string, index: number, data: any) {
    this.removeItem.emit({ roleName, index, data });
    this.filterApplied = {
      ...this.filterApplied,
      roles: {
        ...this.filterApplied.roles,
        [roleName]: this.selectedItemsMap[roleName].length > 0,
      },
    };
  }

  isRoleItemSelected(item: any, roleName: string): boolean {
    return (this.selectedItemsMap[roleName] || []).some((s: any) => s.parent_user_role_id === item.parent_user_role_id);
  }

  toggleRoleItem(item: any, role: any): void {
    const current = this.selectedItemsMap[role.parent_role_name] || [];
    const value = this.toggleArrayItem(current, item, (a, b) => a.parent_user_role_id === b.parent_user_role_id);
    this.roleSelected({ value }, role);
  }

  onRoleSearchInput(item: any, value: string): void {
    this.roleSearchValues[item.parent_role_name] = value;
    this.roleSearch.emit({ roleName: item.parent_role_name, search: value });
    this.cdr.markForCheck();
  }

  clearRoleSearch(item: any): void {
    this.onRoleSearchInput(item, '');
  }

  /** Clears selections for one parent role only; other filter types are untouched. */
  clearRoleSelections(role: any): void {
    this.roleSelected({ value: [] }, role);
    this.cdr.markForCheck();
  }

  filteredParentUserRoles(role: any): any[] {
    const all = role?.parent_user_roles ?? [];
    const term = (this.roleSearchValues[role?.parent_role_name] ?? '')
      .trim()
      .toLowerCase();
    if (!term) {
      return all;
    }
    return all.filter((option: any) =>
      (option?.parent_role_linked_field_value ?? '')
        .toString()
        .toLowerCase()
        .includes(term),
    );
  }

  hasRoleSearchTerm(role: any): boolean {
    return !!(this.roleSearchValues[role?.parent_role_name] ?? '').trim();
  }

  onRoleListScroll(item: any, event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      const role = item.parent_role_name;
      if (this.parentRoleHasMore[role] && !this.parentRoleLoading[role]) {
        this.loadMoreRoles.emit(role);
      }
    }
  }

  clearFilters() {
    // Reset all filterApplied values to false
    this.filterApplied = {
      category: false,
      stage: false,
      venue: false,
      table: false,
      status: false,
      time: false,
      roles: {},
    };
    this.timeFilterStateChange.emit(false);
    this.clear_filters.emit();
  }
  applyFilters() {
    this.apply_changes.emit();
  }

  onEventSelection(event: any, index: number) {
    this.eventSelection.emit({ event, index });
  }

  onCategorySelection(cat: any) {
    this.categorySelection.emit(cat);
  }

  onStatusChange(event: any) {
    this.statusSelectionChange.emit(event);
  }

  remove_item(item: any) {
    this.removeStatusItem.emit(item);
  }
  onYearSelected(event: Date, picker: any, item: any) {
    const year = event.getFullYear().toString();
    const option = { parent_role_linked_field_value: year, parent_user_role_id: year };
    const current = this.selectedItemsMap[item.parent_role_name] || [];
    if (!current.some((s: any) => s.parent_user_role_id === year)) {
      const newValue = [...current, option];
      this.roleSelected({ value: newValue }, item);
    }
    picker.close();
  }

  // Left-nav tab management ---------------------------------------------------
  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  /** Activate a preferred tab (from applied-chip edit) and fetch role data if needed. */
  private applyPreferredTab(tab: string): void {
    if (!tab || !this.isTabAvailable(tab)) {
      return;
    }
    this.activeTab = tab;
    if (tab.startsWith('role_')) {
      const roleName = tab.slice('role_'.length);
      if (roleName && roleName !== 'YOB') {
        this.roleClicked.emit(roleName);
      }
    }
    this.cdr.markForCheck();
  }

  /**
   * Vertical tablist keyboard navigation (WAI-ARIA): arrow/Home/End move focus
   * between tabs and activate them, since only the active tab is in the tab
   * order (roving tabindex).
   */
  onTablistKeydown(event: KeyboardEvent): void {
    const navKeys = [
      'ArrowDown',
      'ArrowUp',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];
    if (!navKeys.includes(event.key)) {
      return;
    }

    const tablist = event.currentTarget as HTMLElement;
    const tabs = Array.from(
      tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
    ).filter((tab) => !tab.disabled);
    if (!tabs.length) {
      return;
    }

    const currentIndex = tabs.findIndex(
      (tab) => tab === document.activeElement,
    );
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = currentIndex <= 0 ? tabs.length - 1 : currentIndex - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    nextTab.focus();
    nextTab.click();
  }

  selectRoleTab(item: any): void {
    const tabId = 'role_' + item.parent_role_name;
    if (this.activeTab !== tabId) {
      this.activeTab = tabId;
      // YOB uses a year-picker, not the paginated/searchable role list, so it
      // never needs a fetch-on-open.
      if (item.parent_role_name !== 'YOB') {
        this.roleClicked.emit(item.parent_role_name);
      }
    }
  }

  isTabAvailable(tab: string): boolean {
    if (!tab) return false;
    if (tab === 'category') return !!this.filterConfig?.category;
    if (tab === 'stage') return !!this.filterConfig?.stage;
    if (tab === 'venue') return !!this.filterConfig?.venue;
    if (tab === 'table') return !!this.filterConfig?.table;
    if (tab === 'status') return !!this.filterConfig?.status;
    if (tab === 'time') return !!this.filterConfig?.time;
    if (tab.startsWith('role_')) {
      const roleName = tab.slice('role_'.length);
      return !!this.filterConfig?.roles && (this.parent_role_list || []).some((r) => r.parent_role_name === roleName);
    }
    if (tab === 'video_events' || tab === 'video_status' || tab === 'video_sports') {
      return this.is_video_filter;
    }
    return false;
  }

  getFirstAvailableTab(): string {
    if (this.filterConfig?.category) return 'category';
    if (this.filterConfig?.stage) return 'stage';
    if (this.filterConfig?.venue) return 'venue';
    if (this.filterConfig?.table) return 'table';
    if (this.filterConfig?.status) return 'status';
    if (this.filterConfig?.time) return 'time';
    if (this.filterConfig?.roles && this.parent_role_list?.length > 0) {
      return 'role_' + this.parent_role_list[0].parent_role_name;
    }
    if (this.is_video_filter) return 'video_events';
    return '';
  }

  // Selected-count getters (nav-item badges) -----------------------------------
  getSelectedCategoryCount(): number {
    return this.addedCategories.length;
  }

  getSelectedStageCount(): number {
    return this.addedStages.length;
  }

  getSelectedVenueCount(): number {
    return this.venue_added_list.length;
  }

  getSelectedTableCount(): number {
    return this.addedTable.length;
  }

  getSelectedStatusCount(): number {
    return this.addedStatus.length;
  }

  getSelectedTimeCount(): number {
    return (this.start_time ? 1 : 0) + (this.end_time ? 1 : 0);
  }

  getSelectedRoleCount(role: any): number {
    return (this.selectedItemsMap[role.parent_role_name] || []).length;
  }

  getSelectedVideoEventsCount(): number {
    return this.events_list.filter((e) => e.selected).length;
  }

  getSelectedVideoStatusCount(): number {
    return this.selectedStatuses.length;
  }

  getTotalSelectedFilterCount(): number {
    let total = 0;
    if (this.filterConfig?.category) total += this.getSelectedCategoryCount();
    if (this.filterConfig?.stage) total += this.getSelectedStageCount();
    if (this.filterConfig?.venue) total += this.getSelectedVenueCount();
    if (this.filterConfig?.table) total += this.getSelectedTableCount();
    if (this.filterConfig?.status) total += this.getSelectedStatusCount();
    if (this.filterConfig?.time) total += this.getSelectedTimeCount();
    if (this.filterConfig?.roles) {
      total += (this.parent_role_list || []).reduce((sum, role) => sum + this.getSelectedRoleCount(role), 0);
    }
    if (this.is_video_filter) {
      total += this.getSelectedVideoEventsCount() + this.getSelectedVideoStatusCount();
    }
    return total;
  }

  trackById(index: number, item: any): string | number {
    if (item != null && typeof item === 'object' && 'id' in item && item.id != null) {
      return item.id;
    }
    return index;
  }
}
